import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle, XCircle, Download, CreditCard, FileText, X } from 'lucide-react';
import { formatDisplayMoney } from '../../../utils/formatMoney';
import ModalShell from '../../shared/ModalShell';
import Button from '../../shared/Button';
import Input from '../../shared/Input';

const PaymentSummary = ({
  booking,
  totalPaid,
  depositPaid,
  fullPaymentPaid,
  payments,
  onDownloadInvoice,
  onGenerateReceipt,
  isGeneratingReceipt = false,
  onViewPaymentHistory,
  isPrivateBooking = false,
  onMarkOfflinePaid,
  isMarkingOfflinePaid = false,
  remainingBalance: remainingBalanceProp,
  displayCurrency = 'USD',
  displayLocale = 'en-US',
}) => {
  const money = (n) => formatDisplayMoney(n, displayCurrency, displayLocale);
  const discountAmount = parseFloat(booking?.discount_amount) || 0;
  const storedPayout = parseFloat(booking?.vendor_payout_amount);
  const storedSubtotal = parseFloat(booking?.subtotal_amount);
  // Private bookings: subtotal = gross, vendor_payout = net after discount.
  // Marketplace: payout/subtotal are typically already net; reconstruct gross for display.
  let serviceNet = Number.isFinite(storedPayout)
    ? storedPayout
    : Number.isFinite(storedSubtotal)
      ? Math.max(0, storedSubtotal - (isPrivateBooking ? discountAmount : 0))
      : 0;
  if (
    isPrivateBooking &&
    discountAmount > 0 &&
    Number.isFinite(storedSubtotal) &&
    Number.isFinite(storedPayout) &&
    Math.abs(storedSubtotal - storedPayout) < 0.021
  ) {
    // Legacy: discount set but payout never reduced from gross.
    serviceNet = Math.max(0, Math.round((storedSubtotal - discountAmount) * 100) / 100);
  } else if (!isPrivateBooking && discountAmount > 0 && Number.isFinite(storedSubtotal) && !Number.isFinite(storedPayout)) {
    serviceNet = storedSubtotal;
  }
  const serviceGross =
    discountAmount > 0
      ? isPrivateBooking && Number.isFinite(storedSubtotal)
        ? storedSubtotal
        : Math.round((serviceNet + discountAmount) * 100) / 100
      : serviceNet;
  const vendorPayout = serviceNet;
  const serviceAmountReceived = parseFloat(totalPaid || 0);
  const remainingBalance =
    remainingBalanceProp != null
      ? parseFloat(remainingBalanceProp)
      : Math.max(0, vendorPayout - serviceAmountReceived);
  const isFullyPaid = serviceAmountReceived >= vendorPayout && vendorPayout > 0;
  const showDepositStatus = depositPaid > 0 && !isFullyPaid;

  const [showPayModal, setShowPayModal] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payNotes, setPayNotes] = useState('');
  const [payError, setPayError] = useState('');

  useEffect(() => {
    if (showPayModal) {
      setPayAmount(remainingBalance > 0 ? remainingBalance.toFixed(2) : '');
      setPayNotes('');
      setPayError('');
    }
  }, [showPayModal, remainingBalance]);

  const openPayModal = () => setShowPayModal(true);
  const closePayModal = () => {
    if (!isMarkingOfflinePaid) setShowPayModal(false);
  };

  const submitPayment = () => {
    const amount = Number.parseFloat(payAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setPayError('Enter a payment amount greater than 0.');
      return;
    }
    if (amount > remainingBalance + 0.001) {
      setPayError(`Amount can’t exceed the balance due (${money(remainingBalance)}).`);
      return;
    }
    setPayError('');
    onMarkOfflinePaid?.({
      amount: Math.round(amount * 100) / 100,
      notes: payNotes.trim() || undefined,
    });
  };

  const wasMarkingRef = useRef(false);
  useEffect(() => {
    if (wasMarkingRef.current && !isMarkingOfflinePaid) {
      setShowPayModal(false);
    }
    wasMarkingRef.current = isMarkingOfflinePaid;
  }, [isMarkingOfflinePaid]);

  return (
    <section className="rounded-lg border border-gray-200 bg-surface p-5 sm:p-6 shadow-sm">
      <div className="mb-4">
        <h2 className="text-base font-bold text-gray-900">Payment</h2>
        <p className="text-sm text-gray-500 mt-0.5">Earnings for this booking</p>
      </div>

      <div className="space-y-3">
        <div className="flex justify-between items-baseline gap-3 pb-3 border-b border-gray-100">
          <span className="text-sm font-medium text-gray-600">Service amount</span>
          <span className="text-xl font-bold tracking-tight text-gray-900">
            {money(serviceGross)}
          </span>
        </div>

        {discountAmount > 0 && (
          <>
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-500">
                Discount{booking?.discount_code?.code ? ` (${booking.discount_code.code})` : ''}
              </span>
              <span className="font-medium text-emerald-600">
                −{money(discountAmount)}
              </span>
            </div>
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-500">Amount due</span>
              <span className="font-semibold text-gray-900">{money(vendorPayout)}</span>
            </div>
          </>
        )}

        <div className="flex justify-between items-center gap-3 text-sm">
          <span className="text-gray-500">Total received</span>
          <span className="font-semibold text-gray-900">{money(totalPaid)}</span>
        </div>

        {showDepositStatus && (
          <div className="flex justify-between items-center gap-3 text-sm">
            <span className="text-gray-500">Deposit paid</span>
            <span className="font-medium text-gray-900">{money(depositPaid)}</span>
          </div>
        )}

        {fullPaymentPaid && (
          <div className="flex justify-between items-center gap-3 text-sm">
            <span className="text-gray-500">Full payment</span>
            <span className="font-medium text-gray-900 inline-flex items-center gap-1">
              <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
              Paid
            </span>
          </div>
        )}

        <div className="flex justify-between items-center gap-3 text-sm">
          <span className="text-gray-500">Status</span>
          <span
            className={`font-semibold inline-flex items-center gap-1 ${
              isFullyPaid
                ? 'text-emerald-600'
                : serviceAmountReceived > 0
                  ? 'text-amber-600'
                  : 'text-gray-500'
            }`}
          >
            {isFullyPaid ? (
              <>
                <CheckCircle className="w-3.5 h-3.5" />
                Fully paid
              </>
            ) : serviceAmountReceived > 0 ? (
              <>
                <XCircle className="w-3.5 h-3.5" />
                Partial
              </>
            ) : (
              <>
                <XCircle className="w-3.5 h-3.5" />
                Pending
              </>
            )}
          </span>
        </div>

        {remainingBalance > 0 && (
          <div className="flex justify-between items-center gap-3 pt-3 border-t border-gray-100 text-sm">
            <span className="text-gray-500">Balance due</span>
            <span className="font-semibold text-gray-900">{money(remainingBalance)}</span>
          </div>
        )}
      </div>

      <div className="mt-5 pt-5 border-t border-gray-100 space-y-2.5">
        {isPrivateBooking && !isFullyPaid && onMarkOfflinePaid && (
          <button
            type="button"
            onClick={openPayModal}
            disabled={isMarkingOfflinePaid}
            className="flex items-center justify-center gap-2 w-full px-3 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckCircle className="w-4 h-4" />
            {isMarkingOfflinePaid ? 'Recording payment...' : 'Mark as paid'}
          </button>
        )}
        {isPrivateBooking && !isFullyPaid && (
          <p className="text-xs text-gray-500 text-center leading-relaxed">
            Record a full or partial off-platform payment (cash, bank transfer, etc.) without Stripe.
          </p>
        )}
        {onGenerateReceipt && (
          <button
            type="button"
            onClick={onGenerateReceipt}
            disabled={isGeneratingReceipt}
            className="flex items-center justify-center gap-2 w-full px-3 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-black transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileText className="w-4 h-4" />
            {isGeneratingReceipt ? 'Generating…' : 'Generate receipt'}
          </button>
        )}
        <button
          type="button"
          onClick={onDownloadInvoice}
          className="flex items-center justify-center gap-2 w-full px-3 py-2.5 border border-gray-300 text-gray-900 rounded-xl hover:bg-surface-muted transition-colors text-sm font-medium"
        >
          <Download className="w-4 h-4" />
          Quick print
        </button>
        {payments.length > 0 && (
          <button
            type="button"
            onClick={onViewPaymentHistory}
            className="flex items-center justify-center gap-2 w-full px-3 py-2.5 border border-gray-300 text-gray-900 rounded-xl hover:bg-surface-muted transition-colors text-sm font-medium"
          >
            <CreditCard className="w-4 h-4" />
            Payment history
          </button>
        )}
      </div>

      <ModalShell
        isOpen={showPayModal}
        onClose={closePayModal}
        labelledBy="offline-payment-title"
        panelClassName="max-w-md overflow-hidden rounded-xl border border-border bg-surface shadow-xl"
      >
        <div className="bg-surface">
          <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-3.5">
            <div>
              <h3 id="offline-payment-title" className="text-lg font-semibold text-ink">
                Record payment
              </h3>
              <p className="mt-0.5 text-xs text-ink-tertiary">
                Balance due {money(remainingBalance)}. You can enter a partial amount.
              </p>
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={closePayModal}
              className="rounded-md border border-border p-1.5 text-ink-tertiary hover:bg-surface-muted"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="space-y-4 p-5">
            <Input
              label="Amount paying"
              type="number"
              inputMode="decimal"
              min="0.01"
              step="0.01"
              max={remainingBalance}
              value={payAmount}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '' || raw === '.') {
                  setPayAmount(raw);
                  if (payError) setPayError('');
                  return;
                }
                // Allow intermediate typing like "12." while drafting
                if (!/^\d*\.?\d{0,2}$/.test(raw)) return;

                const num = Number.parseFloat(raw);
                if (!Number.isFinite(num)) {
                  setPayAmount(raw);
                  return;
                }

                const max = Math.round(remainingBalance * 100) / 100;
                if (num > max) {
                  setPayAmount(max.toFixed(2));
                  setPayError(`Amount can’t exceed the balance due (${money(max)}).`);
                  return;
                }

                setPayAmount(raw);
                if (payError) setPayError('');
              }}
              onBlur={() => {
                if (payAmount === '' || payAmount === '.') return;
                const num = Number.parseFloat(payAmount);
                if (!Number.isFinite(num)) return;
                const max = Math.round(remainingBalance * 100) / 100;
                const clamped = Math.min(Math.max(num, 0), max);
                setPayAmount(clamped.toFixed(2));
              }}
              error={payError}
              required
            />
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded-md border border-border px-2.5 py-1 text-xs font-medium text-ink hover:bg-surface-muted"
                onClick={() => setPayAmount(remainingBalance.toFixed(2))}
              >
                Full balance
              </button>
              {remainingBalance > 1 && (
                <button
                  type="button"
                  className="rounded-md border border-border px-2.5 py-1 text-xs font-medium text-ink hover:bg-surface-muted"
                  onClick={() =>
                    setPayAmount((Math.round((remainingBalance / 2) * 100) / 100).toFixed(2))
                  }
                >
                  Half
                </button>
              )}
            </div>
            <Input
              label="Notes (optional)"
              value={payNotes}
              onChange={(e) => setPayNotes(e.target.value)}
              placeholder="Cash, bank transfer, Zelle…"
            />
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" className="rounded-md" onClick={closePayModal}>
                Cancel
              </Button>
              <Button
                className="rounded-md"
                loading={isMarkingOfflinePaid}
                onClick={submitPayment}
              >
                Record {payAmount ? money(Number(payAmount) || 0) : 'payment'}
              </Button>
            </div>
          </div>
        </div>
      </ModalShell>
    </section>
  );
};

export default PaymentSummary;
